import '../styles/AnalyticsPage.css';
import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { calcStats, calcMaxDrawdown, calcConsecWinStreak, fmt$ } from '../utils/helpers.js';
import { EMOTION_COLORS } from '../utils/constants.js';
import { IntelligenceGate } from './FeatureGate.jsx';
import { useFeatureConfig } from '../hooks/useFeatureConfig.js';

/* ── helpers ── */
const pct = (n, tot) => tot ? Math.round(n / tot * 100) : 0;

/* ── responsive hook ── */
function useIsMobile(bp = 700) {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return mobile;
}

/* ── BarRow ── */
function BarRow({ label, value, max, color, suffix = '' }) {
  const w = max > 0 ? Math.min(100, (Math.abs(value) / max) * 100) : 0;
  return (
    <div className="an-bar-row">
      <div className="an-bar-row-head">
        <span className="an-bar-row-label">{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: value >= 0 ? color : 'var(--red)' }}>
          {value >= 0 ? '+' : ''}{typeof value === 'number' ? value.toFixed(value % 1 === 0 ? 0 : 1) : value}{suffix}
        </span>
      </div>
      <div className="an-bar-track">
        <div className="an-bar-fill" style={{ width: `${w}%`, background: value >= 0 ? color : 'var(--red)' }} />
      </div>
    </div>
  );
}

/* ── KPI pill ── */
function Kpi({ label, value, sub, color = 'var(--white)' }) {
  return (
    <div className="an-kpi">
      <div className="an-kpi-bar" style={{ background: `linear-gradient(90deg,${color}44,transparent)` }} />
      <div className="an-kpi-label">{label}</div>
      <div className="an-kpi-value" style={{ color }}>{value}</div>
      {sub && <div className="an-kpi-sub">{sub}</div>}
    </div>
  );
}

/* ── Section header ── */
function SectionHd({ title, sub }) {
  return (
    <div className="an-section-hd">
      <div className="an-section-title">{title}</div>
      {sub && <div className="an-section-sub">{sub}</div>}
    </div>
  );
}

/* ── Panel wrapper ── */
function Panel({ children, style = {} }) {
  return (
    <div className="an-panel" style={style}>
      {children}
    </div>
  );
}

/* ── Drawdown chart ── */
function DrawdownChart({ trades }) {
  const cvRef = useRef(null);
  const draw = useCallback(() => {
    const cv = cvRef.current;
    if (!cv || trades.length < 2) return;
    const dpr = window.devicePixelRatio || 1;
    const W   = cv.parentElement?.offsetWidth || 340;
    const H   = 130;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);

    const sorted = [...trades].sort((a, b) => {
      const d = a.date.localeCompare(b.date); if (d !== 0) return d;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
    let run = 0, peak = 0;
    const equityPts = sorted.map(t => { run += t.result; if (run > peak) peak = run; return run; });

    const pad = { l:44, r:8, t:10, b:22 };
    const n   = equityPts.length;
    const eMin = equityPts.reduce((a, v) => Math.min(a, v), 0);
    const eMax = equityPts.reduce((a, v) => Math.max(a, v), 0.01);
    const eRange = eMax - eMin || 1;
    const xs  = i => pad.l + (i / (n - 1)) * (W - pad.l - pad.r);
    const ysE = v => H - pad.b - ((v - eMin) / eRange) * (H - pad.t - pad.b);
    ctx.clearRect(0, 0, W, H);

    ctx.fillStyle = 'rgba(200,192,168,.45)'; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'right';
    [eMin, eMin + eRange * 0.5, eMax].forEach(v => {
      ctx.fillText(fmt$(v, false), pad.l - 4, ysE(v) + 3);
      ctx.beginPath(); ctx.moveTo(pad.l, ysE(v)); ctx.lineTo(W - pad.r, ysE(v));
      ctx.strokeStyle = 'rgba(255,255,255,.04)'; ctx.lineWidth = 1; ctx.stroke();
    });
    if (eMin < 0 && eMax > 0) {
      ctx.beginPath(); ctx.moveTo(pad.l, ysE(0)); ctx.lineTo(W - pad.r, ysE(0));
      ctx.strokeStyle = 'rgba(255,255,255,.1)'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]); ctx.stroke(); ctx.setLineDash([]);
    }
    ctx.beginPath();
    equityPts.forEach((e, i) => {
      let pk = 0; for (let j = 0; j <= i; j++) if (equityPts[j] > pk) pk = equityPts[j];
      i === 0 ? ctx.moveTo(xs(i), ysE(pk)) : ctx.lineTo(xs(i), ysE(pk));
    });
    for (let i = n - 1; i >= 0; i--) ctx.lineTo(xs(i), ysE(equityPts[i]));
    ctx.closePath(); ctx.fillStyle = 'rgba(217,79,79,.18)'; ctx.fill();

    const isPos = equityPts[n - 1] >= 0;
    const grad  = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, isPos ? 'rgba(61,190,115,.22)' : 'rgba(217,79,79,.22)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.beginPath(); ctx.moveTo(xs(0), ysE(equityPts[0]));
    equityPts.forEach((v, i) => { if (i) ctx.lineTo(xs(i), ysE(v)); });
    ctx.lineTo(xs(n - 1), H - pad.b); ctx.lineTo(xs(0), H - pad.b);
    ctx.closePath(); ctx.fillStyle = grad; ctx.fill();

    ctx.beginPath(); ctx.moveTo(xs(0), ysE(equityPts[0]));
    equityPts.forEach((v, i) => { if (i) ctx.lineTo(xs(i), ysE(v)); });
    ctx.strokeStyle = isPos ? '#3DBE73' : '#D94F4F'; ctx.lineWidth = 1.5; ctx.lineJoin = 'round'; ctx.stroke();

    const peakLine = equityPts.reduce((a, v) => Math.max(a, v), 0);
    if (peakLine > 0) {
      ctx.beginPath(); ctx.moveTo(pad.l, ysE(peakLine)); ctx.lineTo(W - pad.r, ysE(peakLine));
      ctx.strokeStyle = 'rgba(201,168,76,.3)'; ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]); ctx.stroke(); ctx.setLineDash([]);
    }
    ctx.fillStyle = 'rgba(200,192,168,.4)'; ctx.font = '8px Inter,sans-serif'; ctx.textAlign = 'center';
    [0, Math.floor(n / 2), n - 1].forEach(i => {
      ctx.fillText(sorted[i].date.slice(5).replace('-', '/'), xs(i), H - 4);
    });
  }, [trades]);

  useEffect(() => { draw(); window.addEventListener('resize', draw); return () => window.removeEventListener('resize', draw); }, [draw]);

  if (trades.length < 2) return <div className="an-chart-empty">Need at least 2 trades</div>;
  return <div className="an-canvas-wrap"><canvas ref={cvRef} style={{ display: 'block' }} /></div>;
}

/* ── Time × DOW Heatmap ── */
function TimeHeatmap({ trades }) {
  const DAYS  = ['Mon','Tue','Wed','Thu','Fri'];
  const HOURS = Array.from({ length: 24 }, (_, h) => h);
  const [tooltip, setTooltip] = useState(null);

  const grid = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      if (!t.time || !t.date) return;
      const [ty, tm, td] = t.date.split('-').map(Number);
      const rawDow = new Date(ty, tm - 1, td).getDay();
      if (rawDow === 0 || rawDow === 6) return;
      const dow  = rawDow - 1;
      const [h, m] = t.time.split(':').map(Number);
      const half   = m < 30 ? '00' : '30';
      const key    = `${dow}_${h}_${half}`;
      if (!map[key]) map[key] = { pnl: 0, n: 0, wins: 0 };
      map[key].pnl += t.result; map[key].n++;
      if (t.result > 0) map[key].wins++;
    });
    return map;
  }, [trades]);

  const allPnl = Object.values(grid).map(v => v.pnl);
  const maxAbs = Math.max(...allPnl.map(Math.abs), 1);
  const usedHours = new Set(Object.keys(grid).map(k => parseInt(k.split('_')[1])));
  const visHours  = HOURS.filter(h => usedHours.has(h));
  const cellH = 13;

  if (!visHours.length) return (
    <div className="an-heatmap-empty">Add entry times to your trades to unlock the heatmap.</div>
  );

  return (
    <div style={{ position:'relative' }} data-heatmap>
      <div className="an-hm-days-row">
        {DAYS.map(d => <div key={d} className="an-hm-day-label">{d}</div>)}
      </div>
      {visHours.map(h => (
        ['00','30'].map(half => {
          const hasAny = DAYS.some((_, di) => grid[`${di}_${h}_${half}`]);
          if (!hasAny) return null;
          return (
            <div key={`${h}_${half}`} className="an-hm-row">
              <div className="an-hm-time-label">
                {half === '00' ? `${String(h).padStart(2,'0')}:00` : ''}
              </div>
              {DAYS.map((dayName, di) => {
                const cell = grid[`${di}_${h}_${half}`];
                if (!cell) return (
                  <div key={di} className="an-hm-cell-empty" style={{ height: cellH, background: 'rgba(255,255,255,.025)' }} />
                );
                const intensity = Math.abs(cell.pnl) / maxAbs;
                const bg = cell.pnl > 0 ? `rgba(61,190,115,${0.15 + intensity * 0.70})` : `rgba(217,79,79,${0.15 + intensity * 0.70})`;
                return (
                  <div key={di} className="an-hm-cell" style={{ height: cellH, background: bg }}
                    onMouseEnter={e => {
                      e.currentTarget.style.filter = 'brightness(1.5)';
                      const rect = e.currentTarget.getBoundingClientRect();
                      const parentRect = e.currentTarget.closest('[data-heatmap]').getBoundingClientRect();
                      setTooltip({ dayName, h, half, cell, x: rect.left - parentRect.left, y: rect.top - parentRect.top });
                    }}
                    onMouseLeave={e => { e.currentTarget.style.filter = ''; setTooltip(null); }}
                  />
                );
              })}
            </div>
          );
        })
      ))}
      <div className="an-hm-legend">
        <div className="an-hm-legend-item">
          <div className="an-hm-legend-dot" style={{ background:'rgba(61,190,115,.75)' }}/>
          <span className="an-hm-legend-label">Profitable slot</span>
        </div>
        <div className="an-hm-legend-item">
          <div className="an-hm-legend-dot" style={{ background:'rgba(217,79,79,.75)' }}/>
          <span className="an-hm-legend-label">Losing slot</span>
        </div>
      </div>
      {tooltip && (
        <div className="an-time-tooltip" style={{
          top: Math.max(4, tooltip.y - 90), left: Math.min(tooltip.x, 200),
          border: `1px solid ${tooltip.cell.pnl >= 0 ? 'rgba(61,190,115,.35)' : 'rgba(217,79,79,.35)'}`,
        }}>
          <div className="an-time-tooltip-label">{tooltip.dayName} · {String(tooltip.h).padStart(2,'0')}:{tooltip.half}</div>
          <div className="an-time-tooltip-pnl" style={{ color: tooltip.cell.pnl>=0?'var(--green)':'var(--red)' }}>
            {tooltip.cell.pnl>=0?'+':''}{Math.round(tooltip.cell.pnl)} P&L
          </div>
          <div className="an-time-tooltip-meta" style={{ color:'rgba(200,192,168,.5)' }}>
            {tooltip.cell.n} trade{tooltip.cell.n>1?'s':''}<br/>{Math.round(tooltip.cell.wins / tooltip.cell.n * 100)}% win rate
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MONTHLY P&L HEATMAP
══════════════════════════════════════════ */
function MonthlyHeatmap({ trades, isMobile = false }) {
  const [tooltip, setTooltip] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);

  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS_SHORT   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  const years = useMemo(() => {
    const ys = [...new Set(trades.map(t => new Date(t.date).getFullYear()))].sort((a,b)=>b-a);
    return ys.length ? ys : [new Date().getFullYear()];
  }, [trades]);

  const [selYear, setSelYear] = useState(() => years[0]);

  const monthlyData = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const d = new Date(t.date);
      if (d.getFullYear() !== selYear) return;
      const key = d.getMonth();
      if (!map[key]) map[key] = { pnl:0, n:0, wins:0, losses:0, trades:[] };
      map[key].pnl += t.result; map[key].n++;
      if (t.result > 0) map[key].wins++; else if (t.result < 0) map[key].losses++;
      map[key].trades.push(t);
    });
    return map;
  }, [trades, selYear]);

  const dailyData = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const d = new Date(t.date);
      if (d.getFullYear() !== selYear) return;
      const key = t.date;
      if (!map[key]) map[key] = { pnl:0, n:0, wins:0, dow: (d.getDay()+6)%7, month: d.getMonth(), day: d.getDate() };
      map[key].pnl += t.result; map[key].n++;
      if (t.result > 0) map[key].wins++;
    });
    return map;
  }, [trades, selYear]);

  const allMonthPnls = Object.values(monthlyData).map(m => m.pnl);
  const maxAbsPnl    = Math.max(...allMonthPnls.map(Math.abs), 1);
  const yearPnl      = allMonthPnls.reduce((a, b) => a + b, 0);
  const profitMonths = allMonthPnls.filter(p => p > 0).length;
  const lossMonths   = allMonthPnls.filter(p => p < 0).length;
  const bestMonth    = allMonthPnls.length ? Math.max(...allMonthPnls) : 0;
  const worstMonth   = allMonthPnls.length ? Math.min(...allMonthPnls) : 0;

  const cellColor = (pnl, maxAbs) => {
    if (pnl === undefined) return 'rgba(255,255,255,.03)';
    if (pnl === 0) return 'rgba(255,255,255,.06)';
    const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
    if (pnl > 0) return `rgba(61,190,115,${0.15 + intensity * 0.70})`;
    return `rgba(217,79,79,${0.15 + intensity * 0.70})`;
  };

  const allDays = useMemo(() => {
    const days = [];
    const start = new Date(selYear, 0, 1);
    const end   = new Date(selYear, 11, 31);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key  = d.toISOString().slice(0, 10);
      const dow  = (d.getDay() + 6) % 7;
      const month = d.getMonth();
      const data  = dailyData[key];
      days.push({ key, dow, month, day: d.getDate(), data });
    }
    return days;
  }, [selYear, dailyData]);

  const weeks = useMemo(() => {
    const ws = [];
    let cur = [];
    const firstDow = allDays[0]?.dow || 0;
    for (let i = 0; i < firstDow; i++) cur.push(null);
    allDays.forEach(d => {
      cur.push(d);
      if (cur.length === 7) { ws.push(cur); cur = []; }
    });
    if (cur.length) { while (cur.length < 7) cur.push(null); ws.push(cur); }
    return ws;
  }, [allDays]);

  const allDayPnls = Object.values(dailyData).map(d => d.pnl);
  const maxAbsDay  = Math.max(...allDayPnls.map(Math.abs), 1);

  const cellSize = isMobile ? 8 : 11;
  const cellGap  = isMobile ? 1 : 2;
  const labelW   = isMobile ? 18 : 22;

  return (
    <div className="an-heatmap-wrap">
      {/* Year selector + summary strip */}
      <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:16 }}>
        <div className="an-year-row">
          <div className="an-year-btns">
            {years.map(y => (
              <button key={y} onClick={() => setSelYear(y)}
                className={`an-year-btn ${selYear===y?'active':'idle'}`}>{y}</button>
            ))}
          </div>
        </div>
        <div className="an-stats-strip">
          {[
            ['Year P&L',     yearPnl >= 0 ? `+$${Math.round(Math.abs(yearPnl))}` : `-$${Math.round(Math.abs(yearPnl))}`, yearPnl >= 0 ? 'var(--green)' : 'var(--red)'],
            ['Green Months', profitMonths, 'var(--green)'],
            ['Red Months',   lossMonths,   'var(--red)'],
            ['Best Month',   bestMonth > 0 ? `+$${Math.round(bestMonth)}` : '$0',   'var(--green)'],
            ['Worst Month',  worstMonth < 0 ? `-$${Math.round(Math.abs(worstMonth))}` : '$0', 'var(--red)'],
          ].map(([lbl, val, color]) => (
            <div key={lbl} className="an-stat-mini">
              <div className="an-stat-mini-label">{lbl}</div>
              <div className="an-stat-mini-value" style={{ color }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly grid */}
      <div style={{ marginBottom:20 }}>
        <div className="an-month-label-row">Monthly P&L</div>
        <div className="an-monthly-grid">
          {MONTHS_SHORT.map((mon, mi) => {
            const d = monthlyData[mi];
            const bg = cellColor(d?.pnl, maxAbsPnl);
            const isHover = hoverKey === `m${mi}`;
            return (
              <div key={mi}
                onMouseEnter={e => {
                  setHoverKey(`m${mi}`);
                  if (d) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({ type:'month', label: `${mon} ${selYear}`, d, x: rect.left, y: rect.top });
                  }
                }}
                onMouseLeave={() => { setHoverKey(null); setTooltip(null); }}
                className="an-month-cell"
                style={{
                  background: bg,
                  border: `1px solid ${isHover ? (d?.pnl >= 0 ? 'rgba(61,190,115,.5)' : 'rgba(217,79,79,.5)') : 'rgba(255,255,255,.06)'}`,
                  transform: isHover ? 'translateY(-2px)' : 'none',
                  boxShadow: isHover ? '0 4px 16px rgba(0,0,0,.3)' : 'none',
                }}
              >
                <div className="an-month-name">{mon}</div>
                {d ? (
                  <>
                    <div className="an-month-pnl" style={{ color: d.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {d.pnl >= 0 ? '+' : '-'}${Math.round(Math.abs(d.pnl))}
                    </div>
                    <div className="an-month-count">{d.n} trade{d.n !== 1 ? 's' : ''}</div>
                    <div className="an-month-wr-track">
                      <div className="an-month-wr-fill" style={{ width:`${d.n ? (d.wins/d.n)*100 : 0}%`, background: d.pnl >= 0 ? 'rgba(61,190,115,.6)' : 'rgba(217,79,79,.6)' }}/>
                    </div>
                    <div className="an-month-wr-label">{d.n ? Math.round(d.wins/d.n*100) : 0}% WR</div>
                  </>
                ) : (
                  <div className="an-month-empty">—</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily contribution graph */}
      <div>
        <div className="an-daily-label">Daily Activity — {selYear}</div>
        <div className="an-daily-scroll">
          <div className="an-daily-inner">
            <div className="an-day-labels" style={{ gap: cellGap }}>
              {DAYS_SHORT.map((d, i) => (
                <div key={d} className="an-day-label" style={{
                  height: cellSize, lineHeight: `${cellSize}px`, width: labelW,
                  fontSize: isMobile ? 6 : 7,
                  color: i % 2 === 0 ? 'rgba(200,192,168,.35)' : 'transparent',
                }}>{d}</div>
              ))}
            </div>
            {weeks.map((week, wi) => (
              <div key={wi} className="an-week-col" style={{ gap: cellGap, marginRight: isMobile ? 1 : 2 }}>
                {week.map((day, di) => {
                  if (!day) return <div key={di} style={{ width: cellSize, height: cellSize }} />;
                  const bg = day.data ? cellColor(day.data.pnl, maxAbsDay) : 'rgba(255,255,255,.04)';
                  const isH = hoverKey === day.key;
                  return (
                    <div key={di}
                      onMouseEnter={e => {
                        setHoverKey(day.key);
                        if (day.data) {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltip({ type:'day', label: day.key, d: day.data, x: rect.left, y: rect.top });
                        }
                      }}
                      onMouseLeave={() => { setHoverKey(null); setTooltip(null); }}
                      onTouchStart={() => {
                        if (day.data) setTooltip({ type:'day', label: day.key, d: day.data, x: 20, y: 100 });
                      }}
                      style={{
                        width: cellSize, height: cellSize, background: bg,
                        borderRadius: 2, cursor: day.data ? 'crosshair' : 'default',
                        border: isH ? '1px solid rgba(255,255,255,.35)' : '1px solid transparent',
                        transition: 'border .1s',
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className="an-legend-row">
          <span className="an-legend-label">Less</span>
          {[0.15, 0.35, 0.55, 0.75, 0.95].map(a => (
            <div key={a} className="an-legend-dot" style={{ background:`rgba(61,190,115,${a})` }}/>
          ))}
          <span className="an-legend-label">Profit</span>
          <span className="an-legend-sep">·</span>
          {[0.15, 0.35, 0.55, 0.75, 0.95].map(a => (
            <div key={a} className="an-legend-dot" style={{ background:`rgba(217,79,79,${a})` }}/>
          ))}
          <span className="an-legend-label">Loss</span>
        </div>
      </div>

      {tooltip && (
        <div className="an-tooltip" style={{
          top: Math.max(8, tooltip.y - 90), left: Math.min(tooltip.x, window.innerWidth - 180),
          border: `1px solid ${tooltip.d.pnl >= 0 ? 'rgba(61,190,115,.35)' : 'rgba(217,79,79,.35)'}`,
        }}>
          <div className="an-tooltip-label">{tooltip.label}</div>
          <div className="an-tooltip-pnl" style={{ color: tooltip.d.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {tooltip.d.pnl >= 0 ? '+' : '-'}${Math.round(Math.abs(tooltip.d.pnl))}
          </div>
          <div className="an-tooltip-meta">
            {tooltip.d.n} trade{tooltip.d.n > 1 ? 's' : ''}<br/>
            {Math.round(tooltip.d.wins / tooltip.d.n * 100)}% win rate<br/>
            {tooltip.d.wins}W / {tooltip.d.n - tooltip.d.wins}L
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN ANALYTICS PAGE
══════════════════════════════════════════ */
export default function AnalyticsPage({ trades, user, onUpgrade }) {
  const userPlan = user?.plan ?? 'free';
  const { requiredPlans, featureLabel } = useFeatureConfig();

  const gate = (key) => ({
    userPlan,
    requiredPlans: requiredPlans(key),
    label: featureLabel(key),
    onUpgrade,
  });

  const [period, setPeriod] = useState('ALL');
  const isMobile = useIsMobile(700);

  const filtered = useMemo(() => {
    if (period === 'ALL') return trades;
    const now = new Date();
    return trades.filter(t => {
      const diff = (now - new Date(t.date)) / (1000 * 60 * 60 * 24);
      return period === '30D' ? diff <= 30 : period === '90D' ? diff <= 90 : diff <= 365;
    });
  }, [trades, period]);

  const stats     = useMemo(() => calcStats(filtered), [filtered]);
  const winStreak = useMemo(() => calcConsecWinStreak(filtered), [filtered]);

  const ddCalc = useMemo(() => {
    const { maxDD, maxDDPct } = calcMaxDrawdown(filtered);
    const sorted = [...filtered].sort((a, b) => {
      const d = a.date.localeCompare(b.date); if (d !== 0) return d;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
    let peak = 0, equity = 0;
    sorted.forEach(t => { equity += t.result; if (equity > peak) peak = equity; });
    const currentDD    = Math.max(0, peak - equity);
    const currentDDPct = peak > 0 ? (currentDD / peak) * 100 : 0;
    const recoveryPct  = maxDD > 0 ? Math.min(100, Math.round((1 - currentDD / maxDD) * 100)) : 100;
    return { maxDD, maxDDPct, currentDD, currentDDPct, recoveryPct };
  }, [filtered]);

  const { maxDD, maxDDPct, currentDD, currentDDPct, recoveryPct } = ddCalc;

  const pairStats = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      if (!map[t.pair]) map[t.pair] = { pair: t.pair, n: 0, wins: 0, pnl: 0, pips: 0 };
      map[t.pair].n++; map[t.pair].pnl += t.result; map[t.pair].pips += Number(t.pips) || 0;
      if (t.result > 0) map[t.pair].wins++;
    });
    return Object.values(map).sort((a, b) => b.pnl - a.pnl);
  }, [filtered]);

  const stratStats = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = t.strat || 'Untagged';
      if (!map[k]) map[k] = { strat: k, n: 0, wins: 0, pnl: 0 };
      map[k].n++; map[k].pnl += t.result;
      if (t.result > 0) map[k].wins++;
    });
    return Object.values(map).sort((a, b) => b.pnl - a.pnl);
  }, [filtered]);

  const sessionStats = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = t.session || 'Untagged';
      if (!map[k]) map[k] = { session: k, n: 0, wins: 0, pnl: 0 };
      map[k].n++; map[k].pnl += t.result;
      if (t.result > 0) map[k].wins++;
    });
    return Object.values(map).sort((a, b) => b.pnl - a.pnl);
  }, [filtered]);

  const dowStats = useMemo(() => {
    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const map  = {};
    filtered.forEach(t => {
      const [yr, mo, dy] = t.date.split('-').map(Number);
      const d = DAYS[new Date(Date.UTC(yr, mo - 1, dy)).getUTCDay()];
      if (!map[d]) map[d] = { day: d, n: 0, wins: 0, pnl: 0 };
      map[d].n++; map[d].pnl += t.result;
      if (t.result > 0) map[d].wins++;
    });
    return ['Monday','Tuesday','Wednesday','Thursday','Friday'].map(d => map[d] || { day: d, n: 0, wins: 0, pnl: 0 });
  }, [filtered]);

  const emotionStats = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = t.emotion || 'Untagged';
      if (!map[k]) map[k] = { emotion: k, n: 0, wins: 0, pnl: 0 };
      map[k].n++; map[k].pnl += t.result;
      if (t.result > 0) map[k].wins++;
    });
    return Object.values(map).sort((a, b) => b.pnl - a.pnl);
  }, [filtered]);

  const setupList = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = t.strat || 'Unknown';
      if (!map[k]) map[k] = { n:0, wins:0, pnl:0 };
      map[k].n++; map[k].pnl += t.result;
      if (t.result > 0) map[k].wins++;
    });
    return Object.entries(map).map(([k, v]) => ({
      name: k, n: v.n, pnl: v.pnl,
      wr: Math.round(v.wins / v.n * 100),
      grade: v.wins/v.n >= .8 ? 'A+' : v.wins/v.n >= .6 ? 'B' : 'C',
      color: v.wins/v.n >= .8 ? 'var(--green)' : v.wins/v.n >= .6 ? 'var(--gold)' : 'var(--red)',
    })).sort((a, b) => b.wr - a.wr);
  }, [filtered]);

  const lotDist = useMemo(() => {
    const map = {};
    filtered.forEach(t => {
      const k = String(Number(t.lotSize) || 0);
      if (!map[k]) map[k] = { lot: k, n: 0, pnl: 0 };
      map[k].n++; map[k].pnl += t.result;
    });
    return Object.values(map).sort((a, b) => parseFloat(b.lot) - parseFloat(a.lot));
  }, [filtered]);

  const avgRR = filtered.filter(t => (parseFloat(t.actualRR) > 0 ? parseFloat(t.actualRR) : parseFloat(t.rr)) > 0);
  const maxRR = avgRR.length ? Math.max(...avgRR.map(t => parseFloat(t.actualRR) > 0 ? parseFloat(t.actualRR) : parseFloat(t.rr))) : 0;
  const maxPairPnl  = Math.max(...pairStats.map(p => Math.abs(p.pnl)), 1);
  const maxStratPnl = Math.max(...stratStats.map(s => Math.abs(s.pnl)), 1);

  const [subTab, setSubTab] = useState('overview');

  const SUB_TABS = [
    { id:'overview',   label:'Overview',   short:'Over', icon:'📊' },
    { id:'execution',  label:'Execution',  short:'Exec', icon:'⚡' },
    { id:'timing',     label:'Timing',     short:'Time', icon:'🕐' },
    { id:'psychology', label:'Psychology', short:'Psych',icon:'🧠' },
    { id:'risk',       label:'Risk',       short:'Risk', icon:'🛡️' },
  ];

  if (!trades.length) return (
    <div className="an-empty">
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="rgba(201,168,76,.25)" strokeWidth="1.2">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
      <div style={{ textAlign:'center' }}>
        <div className="an-empty-title">No analytics yet</div>
        <div className="an-empty-sub">Log some trades in the Journal to<br/>unlock deep performance analytics.</div>
      </div>
    </div>
  );

  return (
    <div className="an-page">
      <div className="an-inner">

        {/* ── Header ── */}
        <div className="an-header">
          <div>
            <div className="an-header-eyebrow">KFX Portal / Analytics</div>
            <div className="an-header-title">Deep Analytics</div>
            <div className="an-header-sub">Break down your edge — find what actually makes you money.</div>
          </div>
          <div className="an-period-wrap">
            {['30D','90D','1Y','ALL'].map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`an-period-btn ${period===p?'active':'idle'}`}
                onMouseEnter={e => { if (period !== p) e.currentTarget.style.color = 'rgba(240,234,214,.75)'; }}
                onMouseLeave={e => { if (period !== p) e.currentTarget.style.color = 'rgba(240,234,214,.4)'; }}
              >{p}</button>
            ))}
          </div>
        </div>

        {/* ── Top KPIs ── */}
        <div className="an-kpi-grid">
          <Kpi label="Total Trades"  value={stats.total}              sub={`${stats.wins}W / ${stats.losses}L`}          color="var(--white)"/>
          <Kpi label="Net P&L"       value={fmt$(stats.net, false)}   sub="cumulative"                                    color={stats.net >= 0 ? 'var(--green)' : 'var(--red)'}/>
          <Kpi label="Win Rate"      value={`${stats.wr}%`}           sub={`avg win ${fmt$(stats.aw)}`}                   color="var(--gold)"/>
          <Kpi label="Avg R:R"       value={`${stats.avgrr}R`}        sub={`best ${maxRR.toFixed(1)}R`}                   color="var(--blue)"/>
          <Kpi label="Profit Factor" value={stats.pf}                 sub="gross W / gross L"                             color={parseFloat(stats.pf) >= 1.5 ? 'var(--green)' : 'var(--gold)'}/>
          <Kpi label="Max Drawdown"  value={maxDD>0?`-$${Math.round(maxDD)}`:'$0'} sub={`${maxDDPct.toFixed(1)}% of peak`} color="var(--red)"/>
        </div>

        {/* ── Sub-tab bar ── */}
        {isMobile ? (
          <div className="an-tabs-mobile">
            {SUB_TABS.map(t => (
              <button key={t.id} onClick={() => setSubTab(t.id)}
                className={`an-tab-mobile-btn ${subTab===t.id?'active':'idle'}`}>
                <span className="an-tab-mobile-icon">{t.icon}</span>
                <span className="an-tab-mobile-label">{t.short}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="an-tabs-desktop">
            {SUB_TABS.map(t => (
              <button key={t.id} onClick={() => setSubTab(t.id)}
                className={`an-tab-desktop-btn ${subTab===t.id?'active':'idle'}`}>
                <span className="an-tab-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* ══ OVERVIEW TAB ══ */}
        <div style={{ display: subTab === 'overview' ? 'contents' : 'none' }}>
          <IntelligenceGate {...gate('analytics_overview_drawdown')}
            headline="See Your Full Equity Curve" ctaLabel="See Drawdown Zones"
            ctaSubline="Drawdown stats + recovery meter hidden"
            revealHeight={210} wrapperStyle={{ marginBottom:16 }}>
          <Panel style={{ marginBottom:16 }}>
            <div className="an-eq-header">
              <SectionHd title="Equity Curve & Drawdown" sub="Cumulative P&L with drawdown zones highlighted" />
              <div className="an-eq-legend">
                <div className="an-eq-legend-item"><div className="an-eq-dd-swatch"/> DD zone</div>
                <div className="an-eq-legend-item"><div className="an-eq-peak-swatch"/> Peak</div>
              </div>
            </div>
            <DrawdownChart trades={filtered}/>
            <div className="an-dd-grid">
              {[
                ['Max Drawdown', maxDD>0?`-$${Math.round(maxDD)}`:'$0',    `${maxDDPct.toFixed(1)}% of peak`,     'var(--red)'],
                ['Current DD',  currentDD>0?`-$${Math.round(currentDD)}`:'$0', `${currentDDPct.toFixed(1)}% of peak`, currentDD>0?'var(--red)':'var(--green)'],
                ['Recovery',    `${recoveryPct}%`, recoveryPct>=100?'Fully recovered':'In progress', recoveryPct>=100?'var(--green)':'var(--gold)'],
              ].map(([lbl, val, note, color]) => (
                <div key={lbl} className="an-dd-card">
                  <div className="an-dd-card-label">{lbl}</div>
                  <div className="an-dd-card-value" style={{ color }}>{val}</div>
                  <div className="an-dd-card-note">{note}</div>
                </div>
              ))}
            </div>
            <div className="an-recovery-track">
              <div className="an-recovery-fill" style={{ width:`${recoveryPct}%`, background: recoveryPct>=100?'var(--green)':'var(--gold)' }}/>
            </div>
          </Panel>
          </IntelligenceGate>

          <IntelligenceGate {...gate('analytics_overview_heatmap')}
            headline="Spot Your Best & Worst Months" ctaLabel="See Monthly Heatmap"
            ctaSubline="Full year colour-coded by P&L"
            revealHeight={200} wrapperStyle={{ marginBottom:16 }}>
          <Panel style={{ marginBottom:16 }}>
            <SectionHd title="Monthly P&L Heatmap" sub="Year-at-a-glance — every month and every trading day" />
            <MonthlyHeatmap trades={filtered} isMobile={isMobile}/>
          </Panel>
          </IntelligenceGate>
        </div>

        {/* ══ EXECUTION TAB ══ */}
        <div style={{ display: subTab === 'execution' ? 'contents' : 'none' }}>
          <div className="an-two-col" style={{ marginBottom:16 }}>
            <IntelligenceGate {...gate('analytics_execution_strategy')}
              headline="See Which Strategies Make Money" ctaLabel="See Strategy Breakdown"
              revealHeight={150}>
            <Panel>
              <SectionHd title="Strategy P&L Breakdown" sub="Raw performance per strategy" />
              {stratStats.map(s => (
                <div key={s.strat} className="an-stat-row">
                  <div className="an-stat-row-head">
                    <span className="an-stat-row-left">{s.strat}</span>
                    <div className="an-stat-row-right">
                      <span className="an-stat-wr">{pct(s.wins,s.n)}% WR</span>
                      <span style={{ fontSize:11, fontWeight:700, color:s.pnl>=0?'var(--green)':'var(--red)' }}>{fmt$(s.pnl)}</span>
                    </div>
                  </div>
                  <div className="an-stat-track">
                    <div className="an-stat-fill" style={{ width:`${Math.abs(s.pnl)/maxStratPnl*100}%`, background:s.pnl>=0?'var(--green)':'var(--red)' }}/>
                  </div>
                  <div className="an-stat-count">{s.n} trade{s.n!==1?'s':''}</div>
                </div>
              ))}
              {!stratStats.length && <div className="an-no-data">Tag strategies to unlock this.</div>}
            </Panel>
            </IntelligenceGate>

            <IntelligenceGate {...gate('analytics_execution_setup')}
              headline="Grade Every Setup's Quality" ctaLabel="See Setup Grades"
              revealHeight={150}>
            <Panel>
              <SectionHd title="Setup Quality by Strategy" sub="Which setups are actually profitable?" />
              {setupList.map(st => {
                const bgRgb = st.color==='var(--green)' ? '61,190,115' : st.color==='var(--gold)' ? '201,168,76' : '217,79,79';
                return (
                  <div key={st.name} className="an-grade-row">
                    <div className="an-grade-icon" style={{ background:`rgba(${bgRgb},.15)`, border:`1px solid ${st.color}`, color:st.color }}>{st.grade}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div className="an-grade-name">{st.name}</div>
                      <div className="an-grade-bar-track">
                        <div className="an-grade-bar-fill" style={{ width:`${st.wr}%`, background:st.color }}/>
                      </div>
                    </div>
                    <div className="an-grade-pct" style={{ color:st.color }}>{st.wr}%</div>
                  </div>
                );
              })}
              {!setupList.length && <div className="an-no-data">Tag strategies to unlock this.</div>}
            </Panel>
            </IntelligenceGate>
          </div>

          <IntelligenceGate {...gate('analytics_execution_pairs')}
            headline="Find Your Most Profitable Pairs" ctaLabel="See Pair Breakdown"
            revealHeight={180}>
          <Panel>
            <SectionHd title="Performance by Pair" sub="All pairs you've traded, ranked by P&L" />
            <div className="an-two-col">
              {[pairStats.slice(0, Math.ceil(pairStats.length/2)), pairStats.slice(Math.ceil(pairStats.length/2))].map((half, hi) => (
                <div key={hi}>
                  {half.map(p => (
                    <div key={p.pair} className="an-stat-row">
                      <div className="an-stat-row-head">
                        <span className="an-stat-row-left">{p.pair}</span>
                        <div className="an-stat-row-right">
                          <span className="an-stat-wr">{pct(p.wins, p.n)}% WR</span>
                          <span style={{ fontSize:11, fontWeight:700, color:p.pnl>=0?'var(--green)':'var(--red)' }}>{fmt$(p.pnl)}</span>
                        </div>
                      </div>
                      <div className="an-stat-track">
                        <div className="an-stat-fill" style={{ width:`${Math.abs(p.pnl)/maxPairPnl*100}%`, background:p.pnl>=0?'var(--green)':'var(--red)' }}/>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {!pairStats.length && <div className="an-no-data">No data</div>}
          </Panel>
          </IntelligenceGate>
        </div>

        {/* ══ TIMING TAB ══ */}
        <div style={{ display: subTab === 'timing' ? 'contents' : 'none' }}>
          <div className="an-two-col" style={{ marginBottom:16 }}>
            <IntelligenceGate {...gate('analytics_timing_session')}
              headline="Discover Your Best Trading Sessions" ctaLabel="See Session Breakdown"
              revealHeight={150}>
            <Panel>
              <SectionHd title="Performance by Session" sub="Asian, London, NY — where do you win?" />
              {sessionStats.map(s => {
                const maxAbs = Math.max(...sessionStats.map(x => Math.abs(x.pnl)), 1);
                return (
                  <div key={s.session} className="an-session-row">
                    <div className="an-session-head">
                      <span className="an-session-name">{s.session}</span>
                      <div className="an-session-right">
                        <span className="an-session-meta">{s.n} trades · {pct(s.wins,s.n)}% WR</span>
                        <span style={{ fontSize:12, fontWeight:700, color:s.pnl>=0?'var(--green)':'var(--red)', minWidth:50, textAlign:'right' }}>{fmt$(s.pnl)}</span>
                      </div>
                    </div>
                    <div className="an-session-track">
                      <div className="an-session-fill" style={{ width:`${Math.abs(s.pnl)/maxAbs*100}%`, background:s.pnl>=0?'var(--green)':'var(--red)' }}/>
                    </div>
                  </div>
                );
              })}
              {!sessionStats.length && <div className="an-no-data">Tag sessions to unlock this.</div>}
            </Panel>
            </IntelligenceGate>

            <IntelligenceGate {...gate('analytics_timing_dow')}
              headline="Unlock Best Day of Week" ctaLabel="See Daily Breakdown"
              ctaSubline="Spot which days are most profitable for you"
              revealHeight={150}>
            <Panel>
              <SectionHd title="Best Day of Week" sub="Which days generate the most profit?" />
              {dowStats.every(d => d.n === 0) ? (
                <div className="an-no-data" style={{ padding:'12px 0' }}>Tag trade dates to unlock this.</div>
              ) : (
                <>
                  <div className="an-dow-bars">
                    {dowStats.map(d => {
                      const maxAbs = Math.max(...dowStats.map(x => Math.abs(x.pnl)), 1);
                      const h = Math.max(4, Math.abs(d.pnl) / maxAbs * 80);
                      return (
                        <div key={d.day} className="an-dow-bar-col">
                          <div className="an-dow-bar-val" style={{ color: d.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>{d.pnl !== 0 ? fmt$(d.pnl) : ''}</div>
                          <div className="an-dow-bar" style={{ height: h, background: d.pnl >= 0 ? 'rgba(61,190,115,.7)' : 'rgba(217,79,79,.7)' }} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="an-dow-labels">
                    {dowStats.map(d => <div key={d.day} className="an-dow-label">{d.day.slice(0,3)}</div>)}
                  </div>
                </>
              )}
            </Panel>
            </IntelligenceGate>
          </div>

          <IntelligenceGate {...gate('analytics_timing_heatmap')}
            headline="Unlock Time × Day Heatmap" ctaLabel="See Full Heatmap"
            ctaSubline="Pinpoint the exact hour and day your edge is strongest"
            revealHeight={230}>
          <Panel>
            <SectionHd title="Time × Day Heatmap" sub="When are your most profitable trading windows?" />
            <div className="an-heatmap-info">
              Each cell = one time slot on one day of the week. <span style={{ color:'var(--green)' }}>Green</span> = net profitable, <span style={{ color:'var(--red)' }}>Red</span> = net loss. Brighter = bigger P&L. Hover any cell to see stats.
            </div>
            <TimeHeatmap trades={filtered}/>
          </Panel>
          </IntelligenceGate>
        </div>

        {/* ══ PSYCHOLOGY TAB ══ */}
        <div style={{ display: subTab === 'psychology' ? 'contents' : 'none' }}>
          <IntelligenceGate {...gate('analytics_psychology_overview')}
            featureKey="psychology" ctaLabel="See Emotion Breakdown"
            revealHeight={220} wrapperStyle={{ marginBottom:16 }}>
          <Panel style={{ marginBottom:16 }}>
            <SectionHd title="Emotion vs. Performance" sub="How does your mindset affect your results?" />
            <div className="an-emotion-grid">
              {emotionStats.map(e => {
                const color = EMOTION_COLORS[e.emotion] || 'var(--gold)';
                return (
                  <div key={e.emotion} className="an-emotion-card" style={{ background:'var(--dark3)', borderLeft:`3px solid ${color}` }}>
                    <div className="an-emotion-name" style={{ color }}>{e.emotion}</div>
                    <div className="an-emotion-pnl" style={{ color:e.pnl>=0?'var(--green)':'var(--red)' }}>{fmt$(e.pnl)}</div>
                    <div className="an-emotion-count">{e.n} trades</div>
                    <div className="an-emotion-wr-track">
                      <div className="an-emotion-wr-fill" style={{ width:`${pct(e.wins,e.n)}%`, background:color }}/>
                    </div>
                    <div className="an-emotion-wr-label" style={{ color }}>{pct(e.wins,e.n)}% WR</div>
                  </div>
                );
              })}
              {!emotionStats.length && <div className="an-no-data">Tag emotions to unlock this.</div>}
            </div>
          </Panel>
          </IntelligenceGate>

          <div className="an-two-col">
            <IntelligenceGate {...gate('analytics_psychology_winrate')}
              headline="Rank Your Emotional States" ctaLabel="See Win Rate Ranking"
              ctaSubline="Trade at your best — avoid your worst"
              revealHeight={170}>
            <Panel>
              <SectionHd title="Emotion Win Rate Ranking" sub="Best to worst emotional states" />
              {[...emotionStats].sort((a,b) => pct(b.wins,b.n) - pct(a.wins,a.n)).map((e, i) => {
                const color = EMOTION_COLORS[e.emotion] || 'var(--gold)';
                const wr = pct(e.wins, e.n);
                return (
                  <div key={e.emotion} className="an-emo-rank-row">
                    <div className="an-emo-rank-num">#{i+1}</div>
                    <div className="an-emo-rank-body">
                      <div className="an-emo-rank-head">
                        <span className="an-emo-rank-name" style={{ color }}>{e.emotion}</span>
                        <span className="an-emo-rank-cnt">{e.n} trades</span>
                      </div>
                      <div className="an-emo-rank-track">
                        <div className="an-emo-rank-fill" style={{ width:`${wr}%`, background:color }}/>
                      </div>
                    </div>
                    <div className="an-emo-rank-pct" style={{ color }}>{wr}%</div>
                  </div>
                );
              })}
              {!emotionStats.length && <div className="an-no-data">No data yet.</div>}
            </Panel>
            </IntelligenceGate>

            <IntelligenceGate {...gate('analytics_psychology_pnl')}
              headline="Find Your Costliest Emotion" ctaLabel="See P&L Attribution"
              revealHeight={170}>
            <Panel>
              <SectionHd title="Emotion P&L Attribution" sub="Which state made or cost you the most?" />
              {[...emotionStats].sort((a,b) => b.pnl - a.pnl).map(e => {
                const maxPnl = Math.max(...emotionStats.map(x => Math.abs(x.pnl)), 1);
                return (
                  <div key={e.emotion} className="an-emo-pnl-row">
                    <div className="an-emo-pnl-head">
                      <span className="an-emo-pnl-name">{e.emotion}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:e.pnl>=0?'var(--green)':'var(--red)' }}>{fmt$(e.pnl)}</span>
                    </div>
                    <div className="an-emo-pnl-track">
                      <div className="an-emo-pnl-fill" style={{ width:`${Math.abs(e.pnl)/maxPnl*100}%`, background:e.pnl>=0?'var(--green)':'var(--red)' }}/>
                    </div>
                  </div>
                );
              })}
              {!emotionStats.length && <div className="an-no-data">No data yet.</div>}
            </Panel>
            </IntelligenceGate>
          </div>
        </div>

        {/* ══ RISK TAB ══ */}
        <div style={{ display: subTab === 'risk' ? 'contents' : 'none' }}>
          <IntelligenceGate {...gate('analytics_risk_rr')}
            headline="See Your R:R Distribution" ctaLabel="See Risk Breakdown"
            ctaSubline="Are you sizing your trades consistently?"
            revealHeight={190} wrapperStyle={{ marginBottom:16 }}>
          <Panel style={{ marginBottom:16 }}>
            <SectionHd title="Risk & R:R Breakdown" sub="Are you sizing your trades consistently?" />
            <div className="an-rr-grid">
              <div>
                <div className="an-rr-sub-label">R:R Distribution</div>
                {['0–1','1–2','2–3','3+'].map((range, i) => {
                  const [lo, hi] = [[0,1],[1,2],[2,3],[3,999]][i];
                  const cnt = filtered.filter(t => { const r = (parseFloat(t.actualRR) > 0 ? parseFloat(t.actualRR) : parseFloat(t.rr)) || 0; return r >= lo && r < hi; }).length;
                  return <BarRow key={range} label={`R:R ${range}`} value={cnt} max={filtered.length} color="var(--gold)" suffix=" trades" />;
                })}
              </div>
              <div>
                <div className="an-rr-sub-label">Lot Size Distribution</div>
                {lotDist.map(l => <BarRow key={l.lot} label={`${l.lot} lots`} value={l.n} max={filtered.length} color="var(--blue)" suffix=" trades" />)}
              </div>
            </div>
          </Panel>
          </IntelligenceGate>

          <IntelligenceGate {...gate('analytics_risk_winloss')}
            headline="Compare Your Win vs Loss Size" ctaLabel="See Win/Loss Breakdown"
            ctaSubline="Confirm your edge is real"
            revealHeight={180}>
          <Panel>
            <SectionHd title="Win vs. Loss Size" sub="Is your reward bigger than your risk on average?" />
            <div className="an-winloss-grid">
              {[
                ['Avg Win',              stats.aw,                                                                                                          'var(--green)'],
                ['Avg Loss',             Math.abs(stats.al),                                                                                               'var(--red)'],
                ['Largest Win',          filtered.filter(t=>t.result>0).reduce((a,t)=>Math.max(a,t.result),0),                                            'var(--green)'],
                ['Largest Loss',         Math.abs(filtered.filter(t=>t.result<0).reduce((a,t)=>Math.min(a,t.result),0)),                                  'var(--red)'],
                ['Max Consec. Loss',     stats.maxConsec,                                                                                                  'var(--red)'],
                ['Week Streak (no loss)',winStreak,                                                                                                        'var(--gold)'],
              ].map(([lbl, val, color]) => (
                <div key={lbl} className="an-winloss-card">
                  <div className="an-winloss-label">{lbl}</div>
                  <div className="an-winloss-value" style={{ color }}>
                    {typeof val === 'number' && (lbl.includes('Streak') || lbl.includes('Consec')) ? val : fmt$(val, false)}
                  </div>
                </div>
              ))}
            </div>
          </Panel>
          </IntelligenceGate>
        </div>

      </div>
    </div>
  );
}